/**
 * Playing a word game, and making the play count.
 *
 * `generateWordSearch` has existed here with tests from the start, and no client ever
 * rendered a grid. The game screen named the game, showed a banner reading
 * *"Everything you get right here counts toward your review schedule, the same as a
 * lesson"*, and offered a `Back to Words` button — so the one sentence on it was false,
 * and its own completion event sent `correct: null, total: null, items_scheduled: null`
 * with a comment admitting the games kept no score.
 *
 * This is the part that was missing: a selection on a grid becomes a found word, a found
 * word becomes a graded review of the card that word came from, and the batch goes to
 * the same `submitReviewGrades` a lesson uses. That is what the sentence claims, so that
 * is what it has to do.
 *
 * Pure and platform-free, because all three clients have to agree on what counts as a
 * find — a selection the web accepts and Android rejects is the same word scored on one
 * device and not the other.
 */

import type { Direction, PlacedWord, WordSearch } from './wordsearch.js';

export interface Cell {
  readonly row: number;
  readonly col: number;
}

/** A completed drag: the two ends the learner touched. */
export interface Selection {
  readonly from: Cell;
  readonly to: Cell;
}

export interface GameSessionState {
  /** `itemId`s found so far, in the order found. */
  readonly found: readonly string[];
  /** Selections that matched no word, counted but not named: a miss is not an error. */
  readonly misses: number;
  readonly startedAt: number;
}

export function startSession(now: number): GameSessionState {
  return { found: [], misses: 0, startedAt: now };
}

const DELTAS: Readonly<Record<Direction, readonly [number, number]>> = {
  E: [0, 1],
  W: [0, -1],
  S: [1, 0],
  N: [-1, 0],
  SE: [1, 1],
  NE: [-1, 1],
  SW: [1, -1],
  NW: [-1, -1],
};

/** The cells a placed word occupies, so a selection can be compared against it. */
export function cellsOf(placed: PlacedWord): readonly Cell[] {
  const [dr, dc] = DELTAS[placed.direction];
  return [...placed.word].map((_, index) => ({
    row: placed.row + dr * index,
    col: placed.col + dc * index,
  }));
}

/**
 * The word a selection found, or null.
 *
 * A selection matches a word when its ends are that word's ends — in either order,
 * because dragging right-to-left across a word written left-to-right is the same
 * gesture to a learner and refusing it reads as the game being broken rather than
 * strict.
 */
export function wordAt(puzzle: WordSearch, selection: Selection): PlacedWord | null {
  for (const placed of puzzle.placed) {
    const cells = cellsOf(placed);
    const head = cells[0];
    const tail = cells[cells.length - 1];
    if (head === undefined || tail === undefined) continue;
    const forward = sameCell(head, selection.from) && sameCell(tail, selection.to);
    const backward = sameCell(tail, selection.from) && sameCell(head, selection.to);
    if (forward || backward) return placed;
  }
  return null;
}

function sameCell(a: Cell, b: Cell): boolean {
  return a.row === b.row && a.col === b.col;
}

export interface SelectionOutcome {
  readonly state: GameSessionState;
  /** The word just found, or null for a miss. Null for a word already found, too. */
  readonly found: PlacedWord | null;
  /** True when this selection had already been found; the caller should not re-score. */
  readonly duplicate: boolean;
}

/**
 * Applies one selection.
 *
 * Finding the same word twice is explicitly not a second score: a learner who re-drags a
 * word they already found would otherwise inflate their own review history, and the
 * scheduler would read it as two successful recalls.
 */
export function applySelection(
  puzzle: WordSearch,
  state: GameSessionState,
  selection: Selection,
): SelectionOutcome {
  const hit = wordAt(puzzle, selection);
  if (hit === null) {
    return { state: { ...state, misses: state.misses + 1 }, found: null, duplicate: false };
  }
  if (state.found.includes(hit.itemId)) {
    return { state, found: hit, duplicate: true };
  }
  return { state: { ...state, found: [...state.found, hit.itemId] }, found: hit, duplicate: false };
}

export function isComplete(puzzle: WordSearch, state: GameSessionState): boolean {
  return puzzle.placed.every((placed) => state.found.includes(placed.itemId));
}

/**
 * One graded review per word, ready for `submitReviewGrades`.
 *
 * `hintUsed` is false and `correct` is true for every find, which is the honest reading:
 * the learner produced the word from its letters unaided. A word never found is **not**
 * submitted as incorrect — they ran out of time or interest, which is not the same as
 * failing to recall it, and sending it as a lapse would punish someone for stopping.
 */
export interface GameGrade {
  readonly cardId: string;
  readonly modality: 'recognition';
  readonly correct: true;
  readonly elapsedMs: number;
  readonly hintUsed: false;
  readonly reviewedAt: number;
}

export function gradesFor(
  state: GameSessionState,
  cardIdByItemId: Readonly<Record<string, string>>,
  now: number,
): readonly GameGrade[] {
  const elapsed = Math.max(
    1,
    Math.round((now - state.startedAt) / Math.max(1, state.found.length)),
  );
  const grades: GameGrade[] = [];
  for (const itemId of state.found) {
    const cardId = cardIdByItemId[itemId];
    // An item with no card is a word the learner has never been scheduled on — a game
    // built from a collection rather than from the deck. It still counted on screen; it
    // simply has no review row to update.
    if (cardId === undefined) continue;
    grades.push({
      cardId,
      modality: 'recognition',
      correct: true,
      elapsedMs: elapsed,
      hintUsed: false,
      reviewedAt: now,
    });
  }
  return grades;
}

/** What the completion event reports, so the screen's claim is measurable. */
export interface GameResult {
  readonly correct: number;
  readonly total: number;
  readonly itemsScheduled: number;
  readonly durationMs: number;
}

export function resultOf(
  puzzle: WordSearch,
  state: GameSessionState,
  cardIdByItemId: Readonly<Record<string, string>>,
  now: number,
): GameResult {
  return {
    correct: state.found.length,
    total: puzzle.placed.length,
    itemsScheduled: gradesFor(state, cardIdByItemId, now).length,
    durationMs: Math.max(0, now - state.startedAt),
  };
}
